import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import Utils from '../../utils/immersive_utils';
import { PortfolioManagementContext } from '../portfolio-management';
import { IAccountOption } from '../venue-provider';
import { WebSocketContext } from '../websocket-context/websocket-provider';

interface IParamsProvider {
  children: ReactNode;
}

interface IProvider {
  data: any;
  setData: (value: any) => void;
  getData: (
    account: IAccountOption | IAccountOption[],
    accountsAggregated?: IAccountOption[],
    beginDate?: Date,
    endDate?: Date,
  ) => void;
  isLoadingTransactions: boolean;
  lastUpdateTransactions: string;
  setLastUpdateTransactions: (value: string) => void;
}

const TRANSACTIONS_KEY = 'TRANSACTIONS';

export const TransactionsContext = createContext<IProvider>({} as IProvider);

export const TransactionsProvider = (props: IParamsProvider) => {
  const { sendMessage, subscribeToChannel } = useContext(WebSocketContext);
  const { addDataToAccount, addGeneralAggregatedData } = useContext(PortfolioManagementContext);
  const [data, setData] = useState<any>(null);
  const [lastUpdateTransactions, setLastUpdateTransactions] = useState<string>('');
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);

  const lastSevenDays = new Date();
  lastSevenDays.setDate(lastSevenDays.getDate() - 7);

  const handleResponse = (
    response: any,
    accounts: IAccountOption | IAccountOption[],
    exchange: string,
  ) => {
    if (!response) return;

    const processAccountData = (account: IAccountOption, dataResult: any, error: boolean) => {
      const data = !error
        ? exchange === 'otc' ||
          exchange === 'bybit' ||
          exchange === 'bullish' ||
          exchange === 'coincall'
          ? Utils.csvToObject(dataResult?.transactions)
          : Utils.csvToObject(dataResult)
        : [];

      addDataToAccount({
        account,
        dataParent: 'TRANSACTIONS',
        data,
        error,
      });

      setIsLoadingTransactions(false);
    };

    const processAggregatedAccountsData = (data: any, error: boolean, exchangeParam: string) => {
      addGeneralAggregatedData({
        dataParent: 'TRANSACTIONS',
        data: !error ? Utils.csvToObject(data) : [],
        error,
        exchange: exchangeParam,
      });

      setIsLoadingTransactions(false);
    };

    if (response?.result) {
      const isError = false;
      if (Array.isArray(accounts)) {
        processAggregatedAccountsData(response.result, isError, exchange);
      } else {
        processAccountData(accounts, response.result, isError);
      }
    } else if (response?.error) {
      const isError = true;
      if (Array.isArray(accounts)) {
        processAggregatedAccountsData([], isError, exchange);
      } else {
        processAccountData(accounts, [], isError);
      }
    }
  };

  const getData = useCallback(
    (
      accounts: IAccountOption | IAccountOption[],
      accountsAggregated?: IAccountOption[],
      beginDate = lastSevenDays,
      endDate = new Date(),
    ) => {
      const adjustedBeginDate = new Date(beginDate);
      adjustedBeginDate.setUTCHours(0, 0, 0, 0); // Set beginDate to 00:00:00

      const adjustedEndDate = new Date(endDate);
      adjustedEndDate.setUTCHours(23, 59, 0, 0); // Set endDate to 23:59:00

      let params;
      let exchange = '';

      const getMethod = (exchange: string) => {
        if (
          exchange === 'otc' ||
          exchange === 'bybit' ||
          exchange === 'bullish' ||
          exchange === 'coincall'
        ) {
          return 'transactions';
        }

        return exchange === 'deribit' ? 'load_deribit_transactions' : 'okx_transactions';
      };

      const defineCredentials = (account: IAccountOption) => {
        if (account.exchange === 'otc') {
          return { account: account.name, venue: account.venue };
        }
        return { name: account.name };
      };

      const buildParams = (exchange: string, credentials: any, begin: Date, end: Date) => ({
        jsonrpc: '2.0',
        id: `${TRANSACTIONS_KEY}_${exchange.toUpperCase()}`,
        method: getMethod(exchange),
        params: {
          credentials,
          begin,
          end,
        },
      });

      if (Array.isArray(accounts)) {
        const accountNames = accounts.map((account) => ({ name: account.name }));
        exchange = accounts[0].exchange;
        params = buildParams(exchange, accountNames, adjustedBeginDate, adjustedEndDate);
      } else {
        exchange = accounts.exchange;
        params = buildParams(
          exchange,
          defineCredentials(accounts),
          adjustedBeginDate,
          adjustedEndDate,
        );
      }

      setIsLoadingTransactions(true);
      sendMessage(JSON.stringify(params));
      subscribeToChannel(`${TRANSACTIONS_KEY}_${exchange.toUpperCase()}`, (response: any) =>
        handleResponse(response, accounts, exchange),
      );
    },
    [sendMessage],
  );

  return (
    <TransactionsContext.Provider
      value={{
        data,
        setData,
        getData,
        isLoadingTransactions,
        lastUpdateTransactions,
        setLastUpdateTransactions,
      }}
    >
      {props.children}
    </TransactionsContext.Provider>
  );
};
